<template>
  <div id="jci-trainers-list" class="page-container">
    <!-- Overlay Loading -->
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8">
              <div class="d-flex align-center">
                <div class="header-icon-box">
                  <v-icon large color="white">mdi-account-tie</v-icon>
                </div>
                <div class="ml-6">
                  <h1 class="header-title white--text mb-1">{{ PageTitle }}</h1>
                  <p class="header-subtitle white--text mb-0">{{ PageDescription }}</p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <div class="d-flex justify-end flex-wrap gap-2">
                <v-btn
                  @click.prevent="refreshPageData"
                  color="rgba(255,255,255,0.2)"
                  elevation="0"
                  rounded
                  large
                  class="action-btn"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Floating Action Button -->
    <v-speed-dial
      v-model="fab"
      :top="top"
      :bottom="bottom"
      :right="right"
      :left="left"
      :direction="direction"
      :open-on-hover="hover"
      :transition="transition"
      :fixed="true"
      v-if="SpeedDialFlag"
      class="fab-button"
    >
      <template v-slot:activator>
        <v-btn v-model="fab" color="primary" fab elevation="8">
          <v-icon v-if="fab">mdi-close</v-icon>
          <v-icon v-else>mdi-plus-circle</v-icon>
        </v-btn>
      </template>
      <v-btn fab dark small color="success" @click.prevent="addRecord" class="fab-add-btn">
        <v-icon>mdi-account-plus</v-icon>
      </v-btn>
    </v-speed-dial>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewRecordPrompt" persistent max-width="65%">
      <jci-trainers-preview
        :pageTitle="previewRecordPageTitle"
        :previewRecordFlag="previewRecordFlag"
        :recordData="selectedData"
        @hidePreviewRecordPrompt="hidePreviewRecordPrompt"
        v-if="previewRecordPrompt"
      ></jci-trainers-preview>
    </v-dialog>

    <!-- Main Content -->
    <v-form ref="form2" v-model="valid1" lazy-validation v-on:submit.prevent="submitForm">
      <!-- Filter Card -->
      <v-card elevation="8" class="filter-card mb-6">
        <v-card-text class="pa-6">
          <v-row wrap>
            <v-col cols="12" sm="12" lg="3" md="4">
              <label class="filter-label">
                <v-icon small class="filter-icon mr-2">mdi-map-marker-radius</v-icon>
                Select Zone
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="ZoneCodeOptionsLoading"
                :items="ZoneCodeOptions"
                :rules="ZoneCodeRules"
                v-model="ZoneCode"
                outlined
                rounded
                dense
                clearable
                prepend-icon="mdi-domain"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="12" lg="3" md="4">
              <label class="filter-label">
                <v-icon small class="filter-icon mr-2">mdi-school</v-icon>
                Trainer Type
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="TrainerTypeOptionsLoading"
                :items="TrainerTypeOptions"
                v-model="TrainerType"
                outlined
                rounded
                dense
                clearable
                prepend-icon="mdi-briefcase"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="12" lg="3" md="4">
              <label class="filter-label">
                <v-icon small class="filter-icon mr-2">mdi-check-circle</v-icon>
                Status
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="StatusTypeOptionsLoading"
                :items="StatusTypeOptions"
                v-model="StatusType"
                outlined
                rounded
                dense
                clearable
                prepend-icon="mdi-flag"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="12" lg="1" md="4" class="search-col">
              <label class="filter-label" style="visibility: hidden;">Search</label>
              <v-btn
                :disabled="!valid1"
                @click.prevent="searchForm"
                :loading="SearchFlag"
                color="primary"
                elevation="4"
                rounded
                small
                class="search-btn font-weight-bold"
                style="width: 100%;"
              >
                <v-icon small left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Loading State -->
      <div v-if="LoadingFlag" class="text-center py-12">
        <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
        <p class="mt-4 text-h6 primary--text">Loading trainers...</p>
      </div>

      <!-- No Records Alert -->
      <v-card elevation="8" class="mb-4" v-if="!LoadingFlag && tableData1.length === 0">
        <v-card-text class="pa-6">
          <v-alert type="info" dense outlined prominent icon="mdi-information">
            <v-alert-title>No Records Found</v-alert-title>
            No trainer records match your search criteria. Please try adjusting your filters.
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Records Found Section -->
      <v-container class="py-0" v-if="tableData1.length > 0">
        <!-- Records Info Card -->
        <v-card elevation="8" class="records-info-card mb-6">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-icon large color="primary" class="mr-4">mdi-file-document-multiple</v-icon>
                <div>
                  <p class="text-subtitle2 font-weight-bold mb-0">Total Trainers</p>
                  <p class="text-h4 font-weight-bold mb-0 primary--text">{{ tableData1.length }}</p>
                </div>
              </div>

              <!-- Download and Report Buttons -->
              <div class="d-flex align-center gap-2 flex-wrap">
                <json-excel
                  v-if="tableData1.length > 0 && tableOptions1.JsonDownloadButtonFlag == true"
                  :name="tableOptions1.ExcelFileName"
                  :data="tableData1"
                  :fields="tableOptions1.JsonExcelFields"
                >
                  <v-btn color="success" elevation="4" rounded small class="font-weight-bold white--text">
                    <v-icon small left>mdi-download</v-icon>
                    Export Excel
                  </v-btn>
                </json-excel>

                <v-btn
                  v-if="ExcelDownloadFlag == true && ExcelFileFlag == false"
                  @click.prevent="generateReportExcel"
                  :loading="ExcelLoadingFlag"
                  color="warning"
                  elevation="4"
                  rounded
                  small
                  class="font-weight-bold white--text"
                >
                  <v-icon small left>mdi-file-pdf</v-icon>
                  Generate Report
                </v-btn>

                <v-btn
                  v-if="ExcelFileName != '' && ExcelFileFlag == true"
                  :href="ExcelFileName"
                  target="_blank"
                  color="success"
                  elevation="4"
                  rounded
                  small
                  class="font-weight-bold white--text"
                >
                  <v-icon small left>mdi-download</v-icon>
                  Download Report
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Data Table -->
        <v-card elevation="12" class="table-card">
          <div class="table-wrapper">
            <v-data-table
              class="professional-table"
              loading-text="Loading... Please wait"
              v-model="selected"
              :search="search"
              :loading="TableLoadingFlag"
              :item-key="tableOptions1.ItemKey"
              :single-select="tableOptions1.SingleSelectFlag"
              :headers="tableColumns1"
              :items="tableData1"
              :items-per-page="tableOptions1.ItemsPerPage"
              :footer-props="tableOptions1.FooterProps"
              :hide-default-footer="tableOptions1.HideDefaultFooter"
            >
              <!-- Status Column -->
              <template v-slot:item.ActiveStatusTxt="{ item }">
                <v-chip :color="getActiveStatusColor(item.ActiveStatusTxt)" dark class="status-chip">
                  <v-icon small left>mdi-check-circle</v-icon>
                  {{ item.ActiveStatusTxt }}
                </v-chip>
              </template>

              <!-- Member Image Column -->
              <template v-slot:item.ProfilePic="{ item }">
                <div class="profile-pic-container">
                  <img
                    v-if="item.ProfilePic"
                    class="profile-pic"
                    width="80"
                    height="80"
                    :src="item.ProfilePic"
                    loading="lazy"
                  />
                </div>
              </template>

              <!-- Image Column -->
              <template v-slot:item.ImagePath="{ item }">
                <div class="profile-pic-container">
                  <viewer rebuild :images="item.AllImages">
                    <img
                      class="profile-pic"
                      width="80"
                      height="80"
                      :src="src"
                      :key="src"
                      v-for="src in item.AllImages"
                    />
                  </viewer>
                </div>
              </template>

              <!-- Monthly Events Column -->
              <template v-slot:item.MonthlyEventsFlag="{ item }">
                <v-tooltip bottom v-if="item.MonthlyEventsFlag && item.Total > 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click="editData(item, $event)"
                      target="_blank"
                      color="primary"
                      small
                      elevation="4"
                      rounded
                      class="white--text font-weight-bold"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-calendar-check</v-icon>
                      Events
                    </v-btn>
                  </template>
                  <span>{{ item.MonthName }} Events</span>
                </v-tooltip>
              </template>

              <!-- Generate Report Column -->
              <template v-slot:item.FileGenerateFlag="{ index, item }">
                <v-tooltip bottom v-if="item.FileGenerateFlag && item.Total > 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click="generateReport(index, item)"
                      target="_blank"
                      color="warning"
                      small
                      elevation="4"
                      rounded
                      class="white--text font-weight-bold"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-file-pdf</v-icon>
                      Generate
                    </v-btn>
                  </template>
                  <span>Generate {{ item.MonthName }} MRF</span>
                </v-tooltip>
              </template>

              <!-- Download Column -->
              <template v-slot:item.FileDownloadFlag="{ item }">
                <v-tooltip bottom v-if="item.FileDownloadFlag && item.FilePath != ''">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :href="item.FilePath"
                      target="_blank"
                      color="success"
                      small
                      elevation="4"
                      rounded
                      class="white--text font-weight-bold"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-download</v-icon>
                      Download
                    </v-btn>
                  </template>
                  <span>{{ item.MonthName }} MRF Download</span>
                </v-tooltip>
              </template>

              <!-- Actions Column -->
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom v-if="item.PreviewFlag">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="previewData(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="icon-btn"
                    >
                      <v-icon>mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Preview</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-container>
    </v-form>
  </div>
</template>
      
      <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import JciTrainersPreview from "@/view/pages/erp/jci-trainers/JciTrainersPreview.vue";

export default {
  mixins: [common],
  components: {
    JciTrainersPreview,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      TableLoadingFlag: false,
      OverlayLoadingFlag: false,

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      TrainerType: "",
      TrainerTypeOptions: [],
      TrainerTypeOptionsLoading: false,

      StatusType: "",
      StatusTypeOptions: [
        { value: "", text: "--Select--" },
        { value: 0, text: "Rejected" },
        { value: 1, text: "Approved" },
        { value: 2, text: "Pending for Approval" },
        { value: 3, text: "Pending for Conformation" },
      ],
      StatusTypeOptionsLoading: false,

      rows: [],

      Modules: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",

      recordToEdit: "",
      record: {},
      rowData: {},
      addRecordPrompt: false,

      previewRecordFlag: false,
      previewRecordPrompt: false,
      previewRecordPageTitle: "",

      selectedData: {},

      ResultFlag: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() {
    /*
          this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Vuetify", route: "alerts" },
            { title: "Form Inputs & Control", route: "autocompletes" },
            { title: "Fileinptus" },
          ]);
          */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        this.YearName = tr.CurrentJciYearName;
        this.getZoneCodeOptions();
        this.getTrainerTypeOptions();
      }
    },

    ZoneCodeOptions: function () {
      this.ZoneCodeOptionsLoading = false;
    },

    TrainerTypeOptions: function () {
      this.TrainerTypeOptionsLoading = false;
    },
  },
  methods: {
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddDialog() {
      console.log("hideAddDialog called");
      this.addRecordPrompt = false;
      //   this.searchForm();
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    refreshPageData() {
      console.log("refreshPageData called");

      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "trainer_career",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      this.ZoneCodeOptionsLoading = true;
      if (yearCode != "") {
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getTrainerTypeOptions() {
      console.log("getTrainerTypeOptions called");
      this.TrainerTypeOptionsLoading = true;
      var selectbox1_source = "TrainerType";
      var selectbox1_destination = "TrainerTypeOptions";
      var selectbox1_url = "api/trainer-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        CategoryMainId: 5,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },

    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    searchForm() {
      console.log("searchForm is called");

      var message = "";

      var validate1 = this.$refs.form2.validate();
      // var validate1 = true;
      console.log("validate1=" + validate1);

      if (validate1) {
        this.LoadingFlag = true;
        this.tableData1 = [];
        this.ExcelDownloadFlag = false;
        this.ExcelFileName = "";
        this.ExcelFileFlag = false;
        this.Modules = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/trainer-details/lists";
        var upload = {
          UserInterface: 2,
          ZoneCode: this.ZoneCode,
          TrainerType: this.TrainerType,
          StatusType: this.StatusType,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);
            console.log({ records });

            if (flag == 1) {
              var options = records.TableOptions;
              console.log({ options });
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableOptions1 = options;
              thisIns.ExcelDownloadFlag = options.ExcelDownloadButtonFlag;
              thisIns.tableData1 = records.TableData;

              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Kindly Select the required fields";
        this.sweetAlert("error", message, false);
      }
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.previewRecordFlag = true;
        this.previewRecordPageTitle="JCI Trainers Preview";
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
      <style lang="scss" scoped>
// Color Palette
$primary-color: #667eea;
$secondary-color: #764ba2;
$gradient-main: linear-gradient(135deg, $primary-color 0%, $secondary-color 100%);
$success-color: #10b981;
$warning-color: #f59e0b;
$danger-color: #f44336;
$card-shadow: 0 16px 48px rgba(102, 126, 234, 0.15);

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

#jci-trainers-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;
  animation: fadeIn 0.6s ease-in-out;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: $gradient-main !important;
    border-radius: 16px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 1;
    }
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
  }

  .header-title {
    font-size: 32px;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.8s ease-out;
    letter-spacing: 0.5px;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.95;
    animation: fadeInUp 1s ease-out;
    letter-spacing: 0.3px;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // FILTER CARD
  // ============================================
  .filter-card {
    border-radius: 14px !important;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
    animation: slideInLeft 0.8s ease-out;

    .filter-label {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #333;
      font-size: 14px;
      margin-bottom: 8px;

      .filter-icon {
        color: $primary-color;
      }
    }
  }

  .search-btn {
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: $gradient-main !important;
    transition: all 0.3s ease !important;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 24px rgba(102, 126, 234, 0.4) !important;
    }
  }

  // ============================================
  // RECORDS INFO CARD
  // ============================================
  .records-info-card {
    border-radius: 14px !important;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
    animation: slideInLeft 0.8s ease-out;
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 14px !important;
    border: none;
    overflow: hidden;
    animation: fadeInUp 0.8s ease-out 0.2s backwards;

    .table-wrapper {
      padding: 0;
      overflow-x: auto;
    }

    .professional-table {
      ::v-deep {
        // Header Styling
        thead {
          background: $gradient-main;

          th {
            color: white !important;
            font-weight: 700 !important;
            font-size: 14px !important;
            letter-spacing: 0.3px;
            padding: 16px 12px !important;
            border-bottom: 3px solid rgba(255, 255, 255, 0.2) !important;
          }
        }

        // Body Row Styling
        tbody {
          tr {
            border-bottom: 1px solid rgba(102, 126, 234, 0.1);
            transition: all 0.3s ease;

            &:hover {
              background-color: rgba(102, 126, 234, 0.08) !important;
              box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
            }

            td {
              padding: 14px 12px !important;
              color: #555;
              font-size: 14px;
            }
          }
        }

        // Footer Styling
        tfoot {
          background: rgba(102, 126, 234, 0.05);

          tr {
            border-top: 2px solid rgba(102, 126, 234, 0.2) !important;
          }
        }
      }
    }
  }

  // ============================================
  // PROFILE PICTURE
  // ============================================
  .profile-pic-container {
    display: flex;
    align-items: center;
    justify-content: center;

    .profile-pic {
      border-radius: 12px;
      object-fit: cover;
      border: 3px solid $primary-color;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  // ============================================
  // STATUS CHIP
  // ============================================
  .status-chip {
    font-weight: 600 !important;
    letter-spacing: 0.3px;
    padding: 8px 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;

    .v-icon {
      margin-right: 4px !important;
    }
  }

  // ============================================
  // FAB BUTTON
  // ============================================
  .fab-button {
    ::v-deep .v-speed-dial__list {
      gap: 8px;
    }
  }

  .fab-add-btn {
    transition: all 0.3s ease !important;

    &:hover {
      transform: scale(1.1);
    }
  }

  // ============================================
  // ICON BUTTON
  // ============================================
  .icon-btn {
    transition: all 0.3s ease !important;

    &:hover {
      transform: scale(1.15);
    }
  }

  // ============================================
  // UTILITY CLASSES
  // ============================================
  .page-container {
    width: 100%;
  }

  .gap-2 {
    gap: 8px;
  }

  .text-right {
    text-align: right;
  }

  .justify-end {
    justify-content: flex-end;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .justify-space-between {
    justify-content: space-between;
  }

  .align-end {
    align-self: flex-end;
  }

  .ml-6 {
    margin-left: 24px;
  }

  .mr-4 {
    margin-right: 16px;
  }

  .mr-2 {
    margin-right: 8px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .mb-4 {
    margin-bottom: 16px;
  }

  .mb-0 {
    margin-bottom: 0;
  }

  .mb-1 {
    margin-bottom: 4px;
  }

  .py-12 {
    padding-top: 48px;
    padding-bottom: 48px;
  }

  .pa-8 {
    padding: 32px;
  }

  .pa-6 {
    padding: 24px;
  }

  .pa-4 {
    padding: 16px;
  }

  .mt-4 {
    margin-top: 16px;
  }

  .d-flex {
    display: flex;
  }

  .align-center {
    align-items: center;
  }

  .white--text {
    color: white;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .primary--text {
    color: $primary-color;
  }

  .font-weight-bold {
    font-weight: 700;
  }

  .text-h6 {
    font-size: 20px;
  }

  .text-h4 {
    font-size: 34px;
  }

  .text-subtitle2 {
    font-size: 14px;
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 1200px) {
    .header-title {
      font-size: 26px;
    }

    .header-subtitle {
      font-size: 14px;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 10px;

    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 12px;
    }

    .action-btn {
      font-size: 12px !important;
      padding: 8px 12px !important;

      .v-icon {
        font-size: 16px !important;
      }
    }

    .filter-card {
      margin-bottom: 16px;
    }

    .profile-pic {
      width: 60px;
      height: 60px !important;
    }

    .search-btn {
      font-size: 11px !important;
      padding: 8px 12px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 15px 8px;

    .header-card {
      margin-bottom: 12px;
    }

    .header-title {
      font-size: 18px;
    }

    .header-subtitle {
      font-size: 11px;
    }

    .action-btn {
      font-size: 10px !important;
      padding: 6px 10px !important;

      .v-icon {
        font-size: 14px !important;
      }
    }

    .profile-pic {
      width: 50px;
      height: 50px !important;
    }

    .search-btn {
      font-size: 10px !important;
      padding: 8px 10px !important;
    }

    ::v-deep .v-data-table {
      font-size: 12px !important;

      thead th {
        font-size: 12px !important;
        padding: 12px 8px !important;
      }

      tbody td {
        padding: 10px 8px !important;
      }
    }
  }
}
</style>